import { useCallback, useEffect, useMemo, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { StyleSheet } from "react-native";
import { AppStorage } from "../services/AppStorage";

import { phoneWithCountryCode, saveTokens, useLoginAuthenticateUserMutation } from "@stepapp/core";
import { Block, Button, OtpInput, Typography, palette, typographyVariants } from "@stepapp/ui";

import { useTranslation } from "react-i18next";
import useSMSCodeSend from "../hooks/useSMSCodeSend";
import type { LoginFormValues } from "./Modal/LoginModal";

type OneTimePasswordFormProps = {
  phoneNumber: string;
  handleLoginVerification: (profileExists?: boolean) => void;
  setValue: UseFormSetValue<LoginFormValues>;
  loading: boolean;
  setLoading: (v: boolean) => void;
};

const OneTimePasswordForm = ({
  phoneNumber,
  handleLoginVerification,
  setValue,
  setLoading,
  loading,
}: OneTimePasswordFormProps) => {
  const { t } = useTranslation();
  const sendSMSCode = useSMSCodeSend();

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [timeLeft, setTimeLeft] = useState(30);

  const [loginAuthenticateUser] = useLoginAuthenticateUserMutation();

  const disabled = useMemo(() => timeLeft > 0, [timeLeft]);

  const handleSendAgain = useCallback(() => {
    setTimeLeft(30);
    sendSMSCode({ phoneNumber });
  }, [phoneNumber, sendSMSCode]);

  const handleSubmit = useCallback(
    async (code: string) => {
      setLoading(true);

      const { data } = await loginAuthenticateUser({
        variables: {
          code,
          phone_number: phoneWithCountryCode(phoneNumber, AppStorage().get("countryCode")),
        },
      });

      if (data?.solveLoginChallenge?.access_token) {
        setValue("code", code);
        setValue("otpCodeVerified", true);
        const { refresh_token, access_token } = data?.solveLoginChallenge || {};
        saveTokens({ accessToken: access_token, refreshToken: refresh_token, AppStorage });
        handleLoginVerification();
      } else {
        alert(t("validation.smsOrPhoneNumber"));
      }

      setLoading(false);
    },
    [phoneNumber, setValue, handleLoginVerification, loginAuthenticateUser, setLoading, t],
  );

  useEffect(() => {
    let interval: Maybe<NodeJS.Timeout> = null;

    if (disabled) {
      // @ts-ignore: NodeJS.Timeout is not defined in the global scope
      interval = setInterval(() => setTimeLeft((timeLeft) => timeLeft - 1), 1000);
    } else {
      interval && clearInterval(interval);
    }

    return () => {
      interval && clearInterval(interval);
    };
  }, [disabled]);

  const handleCodeValidation = useCallback((code: string) => {
    setButtonDisabled(code?.length < 6);
  }, []);

  return (
    <Block alignCenter flex={1}>
      <Block style={styles.container}>
        <Block paddingVertical={3}>
          <Typography variant="largeTitle">{t("screens.oneTimePasswordForm.title")}</Typography>
        </Block>

        <OtpInput handleSubmit={handleSubmit} numberOfInputs={6} onChange={handleCodeValidation} />

        <Block alignStart paddingVertical={4}>
          <Button
            disabled={disabled}
            label={
              disabled
                ? t("screens.oneTimePasswordForm.sendAgainIn", {
                    countdown: timeLeft,
                  })
                : t("screens.oneTimePasswordForm.sendAgain")
            }
            onPress={handleSendAgain}
            textStyle={[styles.sendAgain, disabled && styles.disabledText]}
            type="inlineLink"
          />
        </Block>

        <Button
          disabled={buttonDisabled}
          label={t("common.continue")}
          loading={loading}
          onPress={() => {}}
          type="primary"
        />
      </Block>
    </Block>
  );
};

export default OneTimePasswordForm;

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  disabledText: {
    ...typographyVariants.body,
    color: palette.font.secondary,
  },
  sendAgain: {
    ...typographyVariants.bodyBold,
    color: palette.primary.main,
  },
});
